import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { AdCreative, serveAds, detectDevice } from '../../api/ads/ads.api';
import { HOME_BANNER } from '../../api/ads/ad-placements';
import { safeHref } from '../../utils/common/safe-url';
import { useMediaQuery } from '../../hooks/use-media-query';
import { Advertisement } from '../../pages/profile/components/Advertisement';
import { AdOptionsMenu, getHiddenIds, recordAdClick, useAdImpression } from './ad-tracking';
import { adViewerKey } from './ad-viewer-key';
import { useViewerProStatus } from './use-viewer-pro';

/** Cột phải trang chủ chỉ hiện từ `xl` (1280px). Dưới mức đó giữ banner MAVN như cũ, không hỏi máy chủ quảng cáo. */
const HOME_BANNER_MIN_WIDTH = 1280;
/** Máy chủ quảng cáo trả lời chậm hơn mức này thì dùng quảng cáo nhà, bỏ qua câu trả lời đến muộn. */
export const HOME_BANNER_SERVE_TIMEOUT_MS = 3000;
/** Chờ biết người xem có Cab+ Pro hay không tối đa chừng này rồi vẫn hỏi quảng cáo (máy chủ tự trả rỗng cho Pro). */
export const HOME_BANNER_PRO_WAIT_MS = 3000;

// Cùng kích thước với ảnh MAVN để ô không đổi cỡ khi đổi qua lại giữa quảng cáo thật và quảng cáo nhà.
const SLOT_CLS = 'relative w-full md:w-[363px]';
const IMAGE_CLS = 'h-auto w-full aspect-[363/488]';

/** Giữ chỗ đúng kích thước ô trong lúc chờ, để bố cục không nhảy. */
const SlotPlaceholder: React.FC = () => (
  <div aria-hidden className={SLOT_CLS} data-testid="home-banner-loading">
    <div className={IMAGE_CLS} />
  </div>
);

type SlotState = { kind: 'loading' } | { kind: 'ad'; ad: AdCreative } | { kind: 'house' };

const HomeBannerSlot: React.FC = () => {
  const [state, setState] = useState<SlotState>({ kind: 'loading' });
  // Chỉ tính hiển thị khi ảnh đã tải được: ảnh hỏng thì đổi sang quảng cáo nhà, không bao giờ ghi lượt.
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let settled = false;
    const settle = (next: SlotState) => {
      if (settled) return;
      settled = true;
      setState(next);
    };
    // Quá hạn thì dùng quảng cáo nhà; câu trả lời đến sau đó bị bỏ qua.
    const timer = setTimeout(() => settle({ kind: 'house' }), HOME_BANNER_SERVE_TIMEOUT_MS);
    serveAds({ placement: HOME_BANNER, count: 1, device: detectDevice() })
      .then((res) => {
        const ad = res?.[0];
        // Không có quảng cáo, quảng cáo thiếu ảnh hoặc người xem đã ẩn nó: dùng banner MAVN làm quảng cáo nhà.
        settle(ad?.imageUrl && !getHiddenIds().includes(ad.campaignId) ? { kind: 'ad', ad } : { kind: 'house' });
      })
      .catch(() => settle({ kind: 'house' })) // lỗi hoặc khách bị 401: vẫn giữ ô bằng quảng cáo nhà
      .finally(() => clearTimeout(timer));
    return () => {
      settled = true; // gỡ ô rồi thì không cập nhật nữa
      clearTimeout(timer);
    };
  }, []);

  const ad = state.kind === 'ad' ? state.ad : null;
  // Đo hiển thị và click giống hệt thẻ quảng cáo trong bảng tin (≥ 50% trong 1 giây, click kèm vé phục vụ).
  useAdImpression(ad && loaded ? ad : null, ref);

  if (state.kind === 'house') return <Advertisement />;
  if (!ad) return <SlotPlaceholder />;

  return (
    <div ref={ref} className={SLOT_CLS} data-testid="home-banner-ad">
      <a
        href={safeHref(ad.landingUrl)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.preventDefault();
          recordAdClick(ad);
        }}
        className="block cursor-pointer"
      >
        <img
          className={`${IMAGE_CLS} object-cover`}
          src={ad.imageUrl}
          alt={ad.title || 'Quảng cáo'}
          onLoad={() => setLoaded(true)}
          onError={() => setState({ kind: 'house' })}
        />
      </a>
      <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">
        Quảng cáo
      </span>
      <div className="absolute right-2 top-2">
        <AdOptionsMenu
          ad={ad}
          onHidden={() => setState({ kind: 'house' })}
          buttonClassName="p-1 rounded-full bg-black/50 text-white hover:bg-black/70"
        />
      </div>
    </div>
  );
};

/**
 * Banner lớn ở cột phải trang chủ, lấy từ Cab Ads (vị trí HomeBanner). Không có quảng cáo thì hiện banner MAVN như cũ.
 * Người xem có Cab+ Pro không thấy gì; người đã đăng nhập chờ biết chắc có Pro hay không rồi mới hỏi quảng cáo (khách hỏi
 * ngay). Đổi người xem (khách đăng nhập không tải lại trang) thì dựng lại ô để serve lại, vé phục vụ gắn đúng người xem mới;
 * hồ sơ trong Redux đổi làm ô vẽ lại, lúc đó đọc lại khoá.
 */
const HomeBannerAd: React.FC = () => {
  const wide = useMediaQuery(HOME_BANNER_MIN_WIDTH);
  const userId = useSelector((s: RootState) => s.features.auth.profile?.userId);
  const { isPro, known } = useViewerProStatus();
  const viewerKey = adViewerKey();
  // Có token mà chưa có hồ sơ cũng là đang chờ: hồ sơ đến sau token.
  const waitingPro = viewerKey === 'user' && (!userId || !known);
  const [gaveUp, setGaveUp] = useState(false);
  const waiting = waitingPro && !gaveUp;

  useEffect(() => {
    if (!waiting || !wide) return undefined;
    const t = setTimeout(() => setGaveUp(true), HOME_BANNER_PRO_WAIT_MS);
    return () => clearTimeout(t);
  }, [waiting, wide]);

  if (!wide) return <Advertisement />;
  if (isPro) return null;
  if (waiting) return <SlotPlaceholder />;
  return <HomeBannerSlot key={viewerKey} />;
};

export default HomeBannerAd;
